/* eslint-disable @next/next/no-img-element */
/* eslint-disable no-param-reassign */
import React from 'react';
import { getCdnImageUrl } from '@utils/cloudflare';
import Image from '@common/Image/ImageFallback';
import Favorite from '@solar/assets/images/action/favorite';
import cn from '@helpers/className';
import StarRating from '@components/SearchExperiment/Yotpo/StarRating';
import styles from './HotelCardV2.module.css';

type HomePageHotelCardProps = {
  name: string;
  rating: string;
  reviews: number;
  location: string;
  categories: Array<string>;
  images: Array<string>;
  showFavorite?: boolean;
  isFavorited?: boolean;
  isHotSpot?: boolean;
  hotSpotBadge?: React.ReactNode;
  onFavorite?: React.MouseEventHandler;
  onClick?: (e: React.MouseEvent | React.KeyboardEvent) => void;
};

export default function HomePageHotelCard(p: HomePageHotelCardProps) {
  const {
    showFavorite = false,
    isFavorited = false,
    isHotSpot = false,
    hotSpotBadge = null,
    onFavorite = () => {},
    onClick = () => {},
    name,
    rating,
    reviews,
    location,
    categories,
    images,
  } = p;

  return (
    <a
      role="button"
      className="w-full font-rp-basetica cursor-pointer"
      onClick={(e) => {
        e.preventDefault();
        if (onClick) {
          onClick(e);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          if (onClick) {
            onClick(e);
          }
        }
      }}
      tabIndex={0}
    >
      <div
        className={cn({
          'rounded-solar-sm bg-solar-surface-brand-ultramarine': !!isHotSpot,
        })}
      >
        {isHotSpot ? (
          <div className="text-center text-white py-5px text-12">Hot Spot Savings</div>
        ) : null}
        <div className={`${styles.gallery} relative rounded-solar-sm`}>
          <Image
            layout="fill"
            className="rounded-solar-sm"
            src={getCdnImageUrl(500, images[0], '', 95)}
            blurDataUrl={getCdnImageUrl(37, images[0], '', 10)}
            alt={name}
          />

          {showFavorite && (
            <button
              type="button"
              className="absolute top-2 right-2 z-10 swiper-no-swiping"
              onClick={(e) => {
                e.stopPropagation();
                onFavorite(e);
              }}
            >
              <Favorite isFavorited={!!isFavorited} className="w-22 h-22" />
            </button>
          )}

          {isHotSpot && hotSpotBadge ? (
            <div className="absolute z-20 left-2 top-2 rounded-xl bg-solar-surface-brand-ultramarine px-2.5 py-1 h-6 flex items-center gap-5px">
              {hotSpotBadge}
            </div>
          ) : null}
        </div>
      </div>

      {/*-------------------------------------------------------------
      // Hotel Details -----------------------------------------------
      // -------------------------------------------------------------
      */}
      <div className="w-full text-left mt-8px text-14">
        <div className="text-solar-primary whitespace-nowrap truncate font-rp-basetica-semibold">
          {name}
        </div>
        <div className="flex">
          {rating ? (
            <StarRating algoliaRating={parseFloat(rating)} algoliaReviews={reviews} />
          ) : null}
          <div className="text-solar-secondary flex-grow-0 whitespace-nowrap truncate">
            {rating ? (
              <span className="inline-block px-1 text-solar-inverted-secondary text-13">|</span>
            ) : null}
            {location}
          </div>
        </div>
        <div className="text-solar-secondary whitespace-nowrap truncate h-21px">
          {categories.length > 0 && `${categories.join(', ')} access`}
        </div>
      </div>
    </a>
  );
}

HomePageHotelCard.defaultProps = {
  showFavorite: false,
  isFavorited: false,
  isHotSpot: false,
  hotSpotBadge: null,
  onFavorite: () => {},
  onClick: () => {},
};
